import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Heading from "../../../components/Heading/Heading";
import AuthFooter from "../../../components/AuthFooter/AuthFooter";
import { Formik, Form } from "formik";
import AppInput from "../../../common/AppInput";
import AppPhoneInput from "../../../common/AppPhoneInput";
import SelectInput from "../../../common/SelectInput";
import useGetData from "../../../api/useGetData";
import alphabeticalSort from "../../../utils/alphabeticalSort";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Recaptcha from "../../../components/Recaptcha/Recaptcha";
import { useEffect, useRef, useState } from "react";
import { Button, message } from "antd";
import usePostData from "../../../api/usePostData";
import { agentCreateUser } from "../../../utils/validations";

dayjs.extend(customParseFormat);

const currentYear = dayjs().year();
const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
const months = Array.from({ length: 12 }, (_, i) => i + 1);
const days = Array.from({ length: 31 }, (_, i) => i + 1);

const AgentRegisterForm = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation("global");
  const navigate = useNavigate();
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const {
    data: genderData,
    getData: getGenderData,
    loading: loadingGender,
  } = useGetData({
    route: "api/method/airport_transport.api.user.get_gender",
  });
  const {
    data: nationaliyData,
    getData: getNationalityData,
    loading: loadinNationality,
  } = useGetData({
    route: "api/method/airport_transport.api.user.get_nationality",
  });
  const {
    postData: createUser,
    errors: userCreationErrors,
    loading: isCreatingUser,
  } = usePostData({});

  useEffect(() => {
    if (!loadinNationality) {
      getNationalityData({ params: {} });
    }
    if (!loadingGender) {
      getGenderData({ params: {} });
    }
  }, [language]);

  const nationalities = [...nationaliyData].sort(alphabeticalSort);

  const onSubmit = async (values) => {
    if (!recaptchaToken) {
      return message.error(t("recaptchaRequired"));
    }
    const response = await createUser({
      route: "api/method/airport_transport.api.user.register",
      data: {
        email: values.email,
        phone: values.phone,
        first_name: values.firstName,
        last_name: values.lastName,
        dob: `${values.year}-${values.month}-${values.day}`,
        nationality: values.nationality,
        gender: values.gender,
        city: values.city,
        state: values.state,
        postcode: values.postcode,
        street: values.street,
      },
      params: {
        headers: {
          recaptchaToken,
        },
      },
    });
    if (response && response.status === 200) {
      const msg = response?.data?.msg?.toLowerCase();
      if (msg === "user created successfully") {
        message.success(t("user_created_successfully"));
      } else {
        message.success(`${msg}`);
      }
      navigate("/");
    }
  };

  useEffect(() => {
    if (userCreationErrors) {
      const errorMsg = userCreationErrors?.error || "";
      if (errorMsg.toLowerCase() === "mobile number must be unique") {
        message.error(t("errors.mobile_number_must_be_unique"));
      } else if (errorMsg.toLowerCase() === "user already exists") {
        message.error(t("errors.user_already_exists"));
      } else {
        message.error(`${errorMsg}`);
      }
    }
  }, [userCreationErrors]);

  return (
      <div className="sm:w-screen w-full position relative">
        <div className="position absolute left-0 top-0">
          <img
            src="./assets/signin/left_vector.png"
            alt="left_vector"
            className="w-24 h-24 md:w-48 md:h-48"
          />
        </div>
        <div className="position absolute right-0 bottom-0">
          <img
            src="./assets/signin/right_vector.png"
            alt="right_vector"
            className="w-24 md:w-48 h-18 md:h-36"
          />
        </div>
        <div className="z-20 sm:w-screen w-full min-h-screen flex flex-row justify-center items-center">
          <div className="flex flex-col justify-center items-center mt-2">
            <button
              className="mb-4 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <img
                src="./assets/header/LogoSVG.svg"
                alt="Moshrouk Trips"
                className="w-16 h-13"
              />
            </button>
            <div className="mt-4 block w-72 sm:w-[480px] p-3 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
              <div className="w-auto  text-center">
                <Heading
                  title={t("header.create_text")}
                  className={"text-2xl text-[#0E0E11]"}
                />
              </div>
              <div>
                <div className="p-2 md:p-4">
                  <Formik
                    onSubmit={onSubmit}
                    initialValues={{
                      email: "",
                      phone: "",
                      firstName: "",
                      lastName: "",
                      year: undefined,
                      month: undefined,
                      day: undefined,
                      nationality: "",
                      gender: "",
                      city: "",
                      state: "",
                      postcode: "",
                      street: "",
                    }}
                    validationSchema={agentCreateUser}
                  >
                    {({ errors, touched, setFieldValue, values }) => (
                      <Form>
                        <AppInput
                          name="email"
                          type="email"
                          label={t("hero.enter_email_text")}
                          error={errors.email}
                          touched={touched.email}
                        />
                        <AppPhoneInput
                          label={t("hero.enter_phone_text")}
                          name="phone"
                          onChange={(phone) => {
                            setFieldValue("phone", phone);
                          }}
                          value={values.phone}
                          error={errors.phone}
                          touched={touched.phone}
                        />
                        <div className="flex flex-col  md:flex-row justify-between items-center mt-4">
                          <div className={`w-full md:w-1/2 mr-0 md:ms-1`}>
                            <AppInput
                              name="firstName"
                              type="text"
                              label={t("my_profile.first_name_text")}
                              placeholder={t("my_profile.first_name_text")}
                              error={errors.firstName}
                              touched={touched.firstName}
                            />
                          </div>
                          <div className={`w-full md:w-1/2 mr-0 md:ms-1`}>
                            <AppInput
                              name="lastName"
                              type="text"
                              label={t("my_profile.last_name_text")}
                              placeholder={t("my_profile.last_name_text")}
                              error={errors.lastName}
                              touched={touched.lastName}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex flex-col gap-1">
                            <SelectInput
                              name="gender"
                              onChange={(value) =>
                                setFieldValue("gender", value)
                              }
                              placeholder={t("select_gender")}
                              label={t("my_profile.gender_text")}
                              options={genderData.map((g) => ({
                                label: t(g),
                                value: g,
                              }))}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="my-4 flex flex-col gap-1">
                            <SelectInput
                              label={t("my_profile.nationality_text")}
                              onChange={(value) =>
                                setFieldValue("nationality", value)
                              }
                              placeholder={t("select_nationality")}
                              options={nationalities?.map((nationality) => ({
                                label: nationality,
                                value: nationality,
                              }))}
                              name="nationality"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="flex flex-col gap-1">
                            <SelectInput
                              label={t("my_profile.day_text")}
                              onChange={(value) => setFieldValue("day", value)}
                              placeholder={t("select_day")}
                              name="day"
                              options={days.map((day) => ({
                                label: day,
                                value: day,
                              }))}
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <SelectInput
                              label={t("my_profile.month_text")}
                              onChange={(value) =>
                                setFieldValue("month", value)
                              }
                              placeholder={t("select_month")}
                              name="month"
                              options={months.map((month) => ({
                                label: month,
                                value: month,
                              }))}
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <SelectInput
                              label={t("my_profile.year_text")}
                              onChange={(value) => setFieldValue("year", value)}
                              placeholder={t("select_year")}
                              name="year"
                              options={years.map((year) => ({
                                label: year,
                                value: year,
                              }))}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col md:flex-row justify-between items-center gap-2">
                          <div className="w-full md:w-1/2 mr-0 md:mr-1">
                            <AppInput
                              name="city"
                              label={t("my_profile.city_text")}
                              type="text"
                              placeholder={t("my_profile.city_text")}
                              error={errors.city}
                              touched={touched.city}
                            />
                          </div>
                          <div className="w-full md:w-1/2 ml-0 md:ml-1">
                            <AppInput
                              name="state"
                              label={t("my_profile.state_text")}
                              placeholder={t("my_profile.state_text")}
                              type="text"
                              error={errors.state}
                              touched={touched.state}
                            />
                          </div>
                        </div>
                        <div>
                          <AppInput
                            name="postcode"
                            label={t("my_profile.postal_text")}
                            placeholder={t("my_profile.postal_text")}
                            type="text"
                            error={errors.postcode}
                            touched={touched.postcode}
                          />
                        </div>
                        <div>
                          <AppInput
                            name="street"
                            label={t("my_profile.street_address_text")}
                            placeholder={t("my_profile.street_address_text")}
                            type="text"
                            error={errors.street}
                            touched={touched.street}
                          />
                        </div>
                        <div>
                          <Recaptcha
                            onChange={(value) => {
                              setRecaptchaToken(value);
                            }}
                            recaptchaRef={recaptchaRef}
                          />
                        </div>
                        <div className="flex justify-center">
                          <Button
                            className="bg-background_steel_blue cursor-pointer w-full md:w-1/2 text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 mb-2 me-0 md:me-2"
                            type="submit"
                            htmlType="submit"
                            loading={isCreatingUser}
                          >
                            {t("submit_text")}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
            <div className="my-2">
              <AuthFooter />
            </div>
          </div>
        </div>
      </div>
  );
};

export default AgentRegisterForm;
